import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SanctumAuthService } from '@freesixtyfive/auth';
import { getMediaUrl } from '@fsf/util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { EnvironmentToken } from '../../../app.config';
import {
  RECURRENCE_UNITS,
  StoreTaskSchema,
  Task,
  TaskComment,
  TaskExecutionStatus,
  TaskMedia,
  UpdateTaskSchema,
} from '../../../shared/models/task.model';
import { TaskService } from '../../../shared/services/task.service';
import { applyZodErrors } from '../../../shared/util/zod-form-errors';
import {
  getPriorityLabelKey,
  getRecurrenceLabelKey,
  getTaskStatusSeverity,
} from '../task-display';

/**
 * One row in the task's history: either a comment or an event derived
 * from an execution (due, completed, approved).
 */
export type TimelineEntry =
  | { kind: 'comment'; at: string; comment: TaskComment }
  | {
      kind: 'event';
      at: string;
      icon: string;
      labelKey: string;
      params: Record<string, unknown>;
    };

@Component({
  selector: 'app-task-detail',
  imports: [
    CommonModule,
    BadgeModule,
    CardModule,
    CheckboxModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ToastModule,
    ButtonModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    FileUploadModule,
    ImageModule,
    PaginatorModule,
    PanelModule,
    TagModule,
    DatePickerModule,
    ToggleSwitchModule,
  ],
  providers: [MessageService, TaskService, TranslateService],
  templateUrl: './task-detail.component.html',
  styleUrl: '../../defects-page/defect-detail/defect-detail.component.scss',
})
export class TaskDetailComponent implements OnInit {
  @ViewChild('fileUpload') fileUploadComponent!: FileUpload;

  #fb = inject(FormBuilder);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #messageService = inject(MessageService);
  #translateService = inject(TranslateService);
  #authService = inject(SanctumAuthService);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  taskService = inject(TaskService);

  task = signal<Task | null>(null);
  /**
   * create: new task (form). view: existing task, read-only.
   * edit: existing task after "Bearbeiten" was clicked (form).
   */
  mode = signal<'create' | 'view' | 'edit'>('create');
  showForm = computed(() => this.mode() !== 'view');
  newCommentText = signal('');
  timelineFirst = signal(0);
  timelinePerPage = 15;

  readonly statusSeverity = getTaskStatusSeverity;
  readonly priorityLabelKey = getPriorityLabelKey;
  readonly recurrenceLabelKey = getRecurrenceLabelKey;

  canApprove = computed(() =>
    this.#authService.permissions().includes('approve:tasks'),
  );

  currentExecution = computed(() => this.task()?.current_execution ?? null);

  timeline = computed<TimelineEntry[]>(() => {
    const task = this.task();
    if (!task) return [];

    const now = Date.now();
    const entries: TimelineEntry[] = task.comments
      .filter((c) => !!c.datetime)
      .map((comment) => ({
        kind: 'comment' as const,
        at: comment.datetime as string,
        comment,
      }));

    for (const execution of task.executions) {
      // Upcoming due dates are shown in the status box, not in the history.
      if (execution.due_at && new Date(execution.due_at).getTime() <= now) {
        entries.push({
          kind: 'event',
          at: execution.due_at,
          icon: 'fa-regular fa-calendar',
          labelKey: 'task.timeline.due',
          params: {},
        });
      }
      if (execution.completed_at) {
        entries.push({
          kind: 'event',
          at: execution.completed_at,
          icon: 'fa-regular fa-circle-check',
          labelKey:
            execution.missed_count > 0
              ? 'task.timeline.completedMissed'
              : 'task.timeline.completed',
          params: {
            name: execution.completed_by?.name ?? '?',
            count: execution.missed_count,
          },
        });
      }
      if (execution.approved_at) {
        entries.push({
          kind: 'event',
          at: execution.approved_at,
          icon: 'fa-regular fa-badge-check',
          labelKey: 'task.timeline.approved',
          params: { name: execution.approved_by?.name ?? '?' },
        });
      }
    }

    return entries.sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
  });

  paginatedTimeline = computed(() =>
    this.timeline().slice(
      this.timelineFirst(),
      this.timelineFirst() + this.timelinePerPage,
    ),
  );

  form: FormGroup;

  // Labels are translated in the template, so they update once the
  // translation file has loaded (instant() here would return the raw key).
  priorityOptions = ['low', 'medium', 'high', 'critical'].map((key) => ({
    key,
    labelKey: getPriorityLabelKey(key),
  }));

  recurrenceUnitOptions = RECURRENCE_UNITS.map((key) => ({
    key,
    labelKey: `task.recurrence.units.${key}`,
  }));

  responsibleUserOptions = computed(() =>
    this.taskService.responsibleUserOptions().map((u) => ({
      label: u.name,
      value: u.id,
    })),
  );

  constructor() {
    this.form = this.#fb.group({
      title: [''],
      description: [''],
      priority: [null],
      dueAt: [null as Date | null],
      estimatedTime: [null as number | null],
      responsibleUserId: [null as number | null],
      requiresApproval: [false],
      isRecurring: [false],
      recurrenceEvery: [1 as number | null],
      recurrenceUnit: ['day'],
    });

    // The interval is checked against the estimate: changing either side
    // clears a stale "shorter than estimate" error on the interval field.
    for (const name of ['estimatedTime', 'recurrenceUnit', 'isRecurring']) {
      this.form
        .get(name)!
        .valueChanges.pipe(takeUntilDestroyed())
        .subscribe(() =>
          this.form.get('recurrenceEvery')!.updateValueAndValidity(),
        );
    }
  }

  ngOnInit(): void {
    const id = this.#route.snapshot.paramMap.get('id');

    this.taskService.getResponsibleUsers();

    if (id && id !== 'new') {
      this.mode.set('view');
      this.taskService.getTask(Number(id)).subscribe({
        next: (response: any) => this.#setTask(response.data ?? response),
        error: () => this.#error('task.loadError'),
      });
    }
  }

  #setTask(task: Task) {
    this.task.set(task);
    const dueAt = task.current_execution?.due_at;
    this.form.patchValue({
      title: task.title,
      description: task.description,
      priority: task.priority,
      dueAt: dueAt ? new Date(dueAt) : null,
      estimatedTime: task.estimated_time ?? null,
      responsibleUserId: task.responsible_user?.id ?? null,
      requiresApproval: task.requires_approval,
      isRecurring: task.is_recurring,
      recurrenceEvery: task.recurrence_every ?? 1,
      recurrenceUnit: task.recurrence_unit ?? 'day',
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && control.touched);
  }

  getErrorKey(controlName: string): string {
    const control = this.form.get(controlName);
    if (control?.errors?.['zod']) {
      return control.errors['zod'].message;
    }
    return 'validation.invalid';
  }

  onSubmit() {
    const v = this.form.value;
    const isRecurring = !!v.isRecurring;

    const rawData = {
      title: v.title,
      description: v.description?.trim() || null,
      priority: v.priority || null,
      estimated_time: v.estimatedTime != null ? Number(v.estimatedTime) : null,
      responsible_user_id: v.responsibleUserId
        ? Number(v.responsibleUserId)
        : null,
      requires_approval: !!v.requiresApproval,
      recurrence_unit: isRecurring ? v.recurrenceUnit : null,
      recurrence_every:
        isRecurring && v.recurrenceEvery != null
          ? Number(v.recurrenceEvery)
          : null,
      due_at: v.dueAt instanceof Date ? v.dueAt.toISOString() : null,
    };

    const schema = this.mode() === 'edit' ? UpdateTaskSchema : StoreTaskSchema;
    const result = schema.safeParse(rawData);
    if (!result.success) {
      applyZodErrors(result.error, this.form, {
        estimated_time: 'estimatedTime',
        responsible_user_id: 'responsibleUserId',
        recurrence_every: 'recurrenceEvery',
        recurrence_unit: 'recurrenceUnit',
        due_at: 'dueAt',
      });
      return;
    }

    // Empty strings arrive as null (ConvertEmptyStringsToNull), which lets the
    // update clear optional fields such as the assignee or the recurrence.
    const formData = new FormData();
    for (const [key, value] of Object.entries(rawData)) {
      if (typeof value === 'boolean') {
        formData.append(key, value ? '1' : '0');
      } else {
        formData.append(key, value == null ? '' : String(value));
      }
    }

    if (this.fileUploadComponent?.files?.length) {
      for (const file of this.fileUploadComponent.files) {
        formData.append('files[]', file);
      }
    }

    if (this.mode() === 'edit') {
      const task = this.task();
      if (!task) return;

      formData.append('_method', 'PUT');

      this.taskService.updateTask(formData, task.id).subscribe({
        next: (response: any) => {
          this.#setTask(response.data ?? response);
          this.fileUploadComponent?.clear();
          this.mode.set('view');
          this.#success('task.updated');
        },
        error: () => this.#error('task.updateError'),
      });
    } else {
      if (this.newCommentText().trim()) {
        formData.append('comments[0][comment]', this.newCommentText().trim());
        formData.append('comments[0][datetime]', new Date().toISOString());
      }

      this.taskService.createTask(formData).subscribe({
        next: () => {
          this.#success('task.created');
          this.navigateBack();
        },
        error: () => this.#error('task.createError'),
      });
    }
  }

  setStatus(status: TaskExecutionStatus) {
    const task = this.task();
    const execution = this.currentExecution();
    if (!task || !execution) return;

    this.taskService
      .updateExecutionStatus(task.id, execution.id, status)
      .subscribe({
        next: (response: any) => {
          this.#setTask(response.data ?? response);
          this.#success(
            status === 'done' ? 'task.completed' : 'task.statusUpdated',
          );
        },
        error: () => this.#error('task.updateError'),
      });
  }

  onTimelinePageChange(event: any) {
    this.timelineFirst.set(event.first);
  }

  submitComment() {
    const task = this.task();
    const text = this.newCommentText().trim();
    if (!task || !text) return;

    this.taskService.addComment(task.id, text).subscribe({
      next: (response: any) => {
        this.task.set(response.data ?? response);
        this.newCommentText.set('');
        this.timelineFirst.set(0);
      },
      error: () => this.#error('task.commentAddError'),
    });
  }

  removeComment(comment: TaskComment) {
    const task = this.task();
    if (!task) return;

    this.taskService.deleteComment(task.id, comment.id).subscribe({
      next: () => {
        this.task.update((t) =>
          t
            ? { ...t, comments: t.comments.filter((c) => c.id !== comment.id) }
            : t,
        );
      },
      error: () => this.#error('task.commentDeleteError'),
    });
  }

  deleteMedia(media: TaskMedia) {
    const task = this.task();
    if (!task) return;

    this.taskService.deleteMedia(task.id, media.id).subscribe({
      next: () => {
        this.task.update((t) =>
          t ? { ...t, media: t.media.filter((m) => m.id !== media.id) } : t,
        );
      },
      error: () => this.#error('task.mediaDeleteError'),
    });
  }

  /** Uploads images of an existing task right away, independent of the edit form. */
  uploadImages(files: File[], uploader: FileUpload) {
    const task = this.task();
    if (!task || !files.length) return;

    const formData = new FormData();
    for (const file of files) {
      formData.append('files[]', file);
    }
    formData.append('_method', 'PUT');

    this.taskService.updateTask(formData, task.id).subscribe({
      next: (response: any) => {
        const media = (response.data ?? response).media;
        // Only take over the images, so an open edit form keeps its changes.
        this.task.update((t) => (t ? { ...t, media } : t));
        uploader.clear();
      },
      error: () => {
        uploader.clear();
        this.#error('task.uploadError');
      },
    });
  }

  getMediaUrl(url: string): string {
    return getMediaUrl(url, this.#apiUrl);
  }

  navigateBack() {
    this.#router.navigate(['/tasks']);
  }

  startEditing() {
    const task = this.task();
    if (task) this.#setTask(task);
    this.mode.set('edit');
  }

  cancelForm() {
    if (this.mode() === 'create') {
      this.navigateBack();
      return;
    }
    // Discard unsaved changes and return to the read-only view.
    const task = this.task();
    if (task) this.#setTask(task);
    this.form.markAsUntouched();
    this.mode.set('view');
  }

  #success(detailKey: string) {
    this.#messageService.add({
      severity: 'success',
      summary: this.#translateService.instant('label.successHeader'),
      detail: this.#translateService.instant(detailKey),
    });
  }

  #error(detailKey: string) {
    this.#messageService.add({
      severity: 'error',
      summary: this.#translateService.instant('label.errorHeader'),
      detail: this.#translateService.instant(detailKey),
    });
  }
}
